/**
* @prop --uic-tile-box-shadow: default shadow of the tile
* @prop --uic-tile-dragging-outline: outline while dragging is enabled
* @prop --uic-tile-dragging-opacity: opacity while dragging is enabled
* @prop --uic-tile-header-font-size: font size of header label/title
* @prop --uic-tile-header-font-weight: font weight of header label/title
* @prop --uic-tile-header-border-background: background color of the border between header and content
* @prop --uic-tile-header-border-box-shadow: box shadow of the border between header and content
* @prop --uic-tile-background-color: font color of the tile, different for different variants
* @prop --uic-tile-color: background color of the tile, different for different variants
* @prop --uic-tile-border: border of the tile, different for different variants
* @prop --uic-tile-hover-border: border of the tile when hovered, different for different variants
* @prop --uic-tile-hover-color: font color of the tile when hovered, different for different variants;
* @prop --uic-tile-hover-header-border-box-shadow: box shadow of the border between header and content when hovered, different for different variants
* @prop --uic-tile-active-border: border of the tile when active, different for different variants
* @prop --uic-tile-active-background: background color of the tile when active, different for different variants
* @prop --uic-tile-active-color: font color of the tile when active, different for different variants
* @prop --uic-tile-active-dragging-outline: outline while dragging is enabled and tile is active
* @prop --uic-tile-active-dragging-opacity: opacity while dragging is enabled and tile is active
* @prop --uic-tile-focus-outline: outline of the tile when focused, different for different variants
* @prop --uic-tile-focus-outline-offset: outline offset of the tile when focused, different for different variants
* @prop --uic-tile-focus-dragging-outline: outline while dragging is enabled and tile is focused
* @prop --uic-tile-focus-dragging-outline-offset: outline offset while dragging is enabled and tile is focused
*/

uic-tile {
    display: block;
}

.uic-tile {
    display: flex;
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    position: relative;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    box-shadow: var(--uic-tile-box-shadow);

    background: var(--uic-tile-background-color);
    color: var(--uic-tile-color);
    border: var(--uic-tile-border);

    &--dragable {
        cursor: grab;
    }

    &--clickable {
        cursor: pointer;
    }

    &--dragging {
        outline: var(--uic-tile-dragging-outline);
        opacity: var(--uic-tile-dragging-opacity);
        cursor: move;
    }

    &:focus {
        outline: none;
    }

    &--dragable:hover,
    &--clickable:hover {
        border: var(--uic-tile-hover-border);
        color: var(--uic-tile-hover-color);
    }

    &--clickable:hover .uic-tile__border::after,
    &--dragable:hover .uic-tile__border::after {
        box-shadow: var(--uic-tile-hover-header-border-box-shadow);
    }

    &--dragable:active,
    &--clickable:active {
        border: var(--uic-tile-active-border);
        background: var(--uic-tile-active-background);
        color: var(--uic-tile-active-color);
    }

    &--dragable.uic-tile--dragging:active {
        outline: var(--uic-tile-active-dragging-outline);
        opacity: var(--uic-tile-active-dragging-opacity);
        cursor: move;
    }

    &--dragable:focus,
    &--clickable:focus {
        outline: var(--uic-tile-focus-outline);
        outline-offset: var(--uic-tile-focus-outline-offset);
    }

    &--dragable.uic-tile--dragging:focus {
        outline: var(--uic-tile-focus-dragging-outline);
        outline-offset: var(--uic-tile-focus-dragging-outline-offset);
        opacity: 1;
        cursor: move;
    }

    &__header {
        display: flex;
        width: 100%;
        box-sizing: border-box;
        flex-direction: row;
        align-items: center;
        margin-bottom: 8px;
        padding: 13px 0 6px;
    }

    &__close-button {
        margin-right: 8px;
    }

    &__label {
        height: fit-content;
        font-size: var(--uic-tile-header-font-size);
        font-weight: var(--uic-tile-header-font-weight);
    }

    &--outline {
        &.uic-tile--dragable:active,
        &.uic-tile--clickable:active {
            box-shadow: none;
        }

        &.uic-tile--with-content .uic-tile__content {
            height: 100%;
        }

        &.uic-tile--with-content .uic-tile__content-container {
            align-items: center;
            flex-direction: column;
        }
    }

    &--with-content {
        align-items: flex-start;
        justify-content: flex-start;

        .uic-tile__icon {
            margin: 0 0 0 8px;
        }

        .uic-tile__label {
            min-width: 30%;
            flex-grow: 1;
            margin: 0 0 0 14px;
        }

        .uic-tile__icon + .uic-tile__label {
            margin: 0 0 0 8px;
        }

        .uic-tile__content {
            display: flex;
            width: 100%;
            height: 100%;
            position: relative;
            flex: 1;
            flex-direction: row;
            justify-content: center;
            overflow: hidden;
        }

        .uic-tile__content-container {
            display: flex;
            width: 100%;
            height: 100%;
            flex-direction: row;
            justify-content: center;
        }

        .uic-tile__content-container--hidden {
            opacity: 0;
        }

        .uic-tile__loader-container {
            display: flex;
            width: 100%;
            height: 100%;
            position: absolute;
            z-index: var(--uic-tile-loader-container-z-index);
            align-items: center;
            justify-content: center;
        }

        .uic-tile__border {
            display: block;
            width: 100%;
            height: 8px;
            overflow: hidden;
            position: absolute;
            left: 0;
            margin: 0;
        }

        .uic-tile__border::after {
            content: "";
            width: 100%;
            height: 1px;
            position: absolute;
            top: 8px;
            left: 0;
            background: var(--uic-tile-header-border-background);
            box-shadow: var(--uic-tile-header-border-box-shadow);
        }

        &.uic-tile--outline .uic-tile__border {
            display: none;
        }

        &.uic-tile--outline .uic-tile__header {
            display: none;
        }
    }

    // measurement
    @media #{$breakpoint_tablet} {
        .uic-tile__header {
            height: 41px;
        }

        .uic-tile__loader {
            width: 128px;
        }

        .uic-tile__border {
            top: 40px;
        }
    }

    @media #{$breakpoint_desktop} {
        .uic-tile__header {
            height: 49px;
        }

        .uic-tile__icon {
            margin: 4px 0 4px 8px !important;
        }

        .uic-tile__loader {
            width: 154px;
        }

        .uic-tile__border {
            top: 48px;
        }
    }

    @media #{$breakpoint_tablet_small} {
        .uic-tile__header {
            height: 31px;
        }

        .uic-tile__icon {
            margin: 2px 0 2px 8px !important;
        }

        .uic-tile__loader {
            width: 96px;
        }

        .uic-tile__border {
            top: 30px;
        }
    }
}
